
/**
 * App-Verwaltungs-Gates (PERMISSIONS.md §8) — wer darf die globale Listen- bzw.
 * Benutzerverwaltung öffnen? Bewusst ORTHOGONAL zur Punkt-/Projekt-Matrix
 * (§3–§6): diese Helfer ändern KEINE Punkt-/Projekt-Rechte (manager bleibt bei
 * Punkten read+checkout). Single-Source für Route-Guard UND Navbar-Sichtbarkeit,
 * damit beide nie auseinanderlaufen.
 *
 * @param isAdmin App-Verwaltung-Capability (Keycloak-Client-Admin). Im Live-Code
 *                aus `CapabilitiesCacheService.canManage$`. Im Route-Guard wird
 *                `false` übergeben, weil dort der Admin-Pfad asynchron über
 *                `userManagementGuard` (Capability-Load) abgefragt wird.
 * @param can     Rollen-Prädikat, i.d.R. `AuthService.canIbe`.
 */
export function canAccessListenverwaltung(isAdmin: boolean, can: (role: string) => boolean): boolean {
	// Listenverwaltung: Admin ODER Entwickler ODER Manager (User-Entscheidung 2026-06-05).
	return isAdmin || can('entwickler') || can('manager');
}

export function canAccessBenutzerverwaltung(isAdmin: boolean, can: (role: string) => boolean): boolean {
	// Benutzerverwaltung: Admin ODER Entwickler — NICHT Manager (User-Entscheidung 2026-06-05).
	return isAdmin || can('entwickler');
}

/**
 * Druck-Layout-Designer (PERMISSIONS.md §8) — NUR Entwickler (User-Entscheidung
 * 2026-06-28). Bewusst OHNE `isAdmin`: es ist ein Projekt-Feature, kein App-
 * Management. Ändert KEINE Punkt-/Projekt-Rechte. Single-Source für Route-Guard
 * UND Navbar-Ausgrauen, damit beide nie auseinanderlaufen.
 *
 * @param can Rollen-Prädikat, i.d.R. `AuthService.canIbe`.
 */
export function canAccessDruckLayout(can: (role: string) => boolean): boolean {
	return can('entwickler');
}

/**
 * Datenbankverwaltung (PERMISSIONS.md §8) — NUR Entwickler (User-Entscheidung
 * 2026-08-03). Wie der Druck-Layout-Zugang bewusst OHNE `isAdmin`: reines
 * Entwickler-Werkzeug (CouchDB-Admin über den luzula-api-Gateway), ändert KEINE
 * Punkt-/Projekt-Rechte. Eigener top-level Menüpunkt (`admin/databases`), NICHT
 * mehr unter der Benutzerverwaltung. Single-Source für Route-Guard UND Navbar-
 * Sichtbarkeit.
 *
 * @param can Rollen-Prädikat, i.d.R. `AuthService.canIbe`.
 */
export function canAccessDatenbankverwaltung(can: (role: string) => boolean): boolean {
	return can('entwickler');
}

export const noPermissions  = new Map<string,Set<string>>([
	[ 'points', new Set<string>([ ])], 
	[ 'layers', new Set<string>([ ])], 
	[ 'filter', new Set<string>([ ])], 
	[ 'comments', new Set<string>([ ])], 
	[ 'projects', new Set<string>([ ])], 
]);

export const RoleDefinition = new Map<string, Map<string,Set<string>>>([
	[ 'entwickler',  new Map<string,Set<string>>(
		[
			[ 'points', new Set<string>([ 'add', 'read', 'write', 'checkin', 'checkout' ])], 
			[ 'layers', new Set<string>([ 'read' ])], 
			[ 'filter', new Set<string>([ 'use' ])], 
			[ 'comments', new Set<string>([ 'read', 'write' ])], 
			[ 'projects', new Set<string>([ 'manage' ])], 
		])
	], 
	[ 'manager',  new Map<string,Set<string>>(
		[
			[ 'points', new Set<string>([ 'read', 'checkout' ])], 
			[ 'layers', new Set<string>([ 'read' ])], 
			[ 'filter', new Set<string>([ 'use' ])], 
			[ 'comments', new Set<string>([ 'read', 'write' ])], 
			[ 'projects', new Set<string>([ 'manage' ])], 
		])
	], 
	[ 'datennutzer',  new Map<string,Set<string>>(
		[
			[ 'points', new Set<string>([ 'read' ])],
			[ 'layers', new Set<string>([ ])],
			[ 'filter', new Set<string>([ ])],
			[ 'comments', new Set<string>([ ])],
			[ 'projects', new Set<string>([ ])],
		])
	],
	[ 'bearbeiter',  new Map<string,Set<string>>(
		[
			[ 'points', new Set<string>([ 'add', 'read', 'write', 'checkin', 'checkout' ])], 
			[ 'layers', new Set<string>([ 'read' ])], 
			[ 'filter', new Set<string>([ 'use' ])], 
			[ 'comments', new Set<string>([ 'read', 'write' ])], 
			[ 'projects', new Set<string>([ ])], 
		])
	], 
	[ 'kontrolleur',  new Map<string,Set<string>>(
		[
			[ 'points', new Set<string>([ 'read', 'write', 'checkin', 'checkout' ])], 
			[ 'layers', new Set<string>([ 'read' ])], 
			[ 'filter', new Set<string>([ 'use' ])], 
			[ 'comments', new Set<string>([ 'read', 'write' ])], 
			[ 'projects', new Set<string>([ ])], 
		])
	] 
]);
